import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Input, Dropdown, TextArea, CurrencyInput, LocationInput, DatePicker } from '../forms';
import { ExtendedCreateProjectData, ProjectTemplate, ProjectLocation } from '@/types';
import { templateService } from '@/services/templateService';
import { getMinDate, isDateInPast } from '@/utils/configUtils';

interface ProjectDetailsStepProps {
  data: Partial<ExtendedCreateProjectData>;
  updateData: (data: Partial<ExtendedCreateProjectData>) => void;
  setValid: (isValid: boolean) => void;
}

const ProjectDetailsStep: React.FC<ProjectDetailsStepProps> = ({ data, updateData, setValid }) => {
  const { t } = useTranslation();

  // State management
  const [templates, setTemplates] = useState<ProjectTemplate[]>([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Memoized template options
  const templateOptions = useMemo(
    () => [
      { value: '', label: t('projects.creation.projectTemplate.customProject') },
      ...templates.map(template => ({
        value: template.id,
        label: template.name,
        description: template.description,
      })),
    ],
    [templates, t]
  );

  // Load templates on component mount
  useEffect(() => {
    const loadTemplates = async () => {
      setIsLoadingTemplates(true);
      try {
        const response = await templateService.getTemplates();
        if (response.success && response.data) {
          setTemplates(response.data);
        }
      } catch (error) {
        console.error('Failed to load templates:', error);
      } finally {
        setIsLoadingTemplates(false);
      }
    };

    loadTemplates();
  }, []);

  // Validation function with memoization
  const validateForm = useCallback(() => {
    const newErrors: Record<string, string> = {};

    const name = data.name;
    if (!name || typeof name !== 'string' || name.trim().length === 0) {
      newErrors.name = t('projects.creation.validation.projectNameRequired');
    }

    // Description validation - minimum 10 characters required
    const description = data.description;
    if (!description || typeof description !== 'string' || description.trim().length === 0) {
      newErrors.description = t('projects.creation.validation.descriptionRequired');
    } else if (description.trim().length < 10) {
      newErrors.description = t('projects.creation.validation.descriptionMinLength');
    } else if (description.trim().length > 2000) {
      newErrors.description = t('projects.creation.validation.descriptionMaxLength');
    }

    // Check location object instead of simple address string
    if (!data.location?.address || data.location.address.trim().length === 0) {
      newErrors.address = t('projects.creation.validation.projectAddressRequired');
    }

    if (!data.startDate) {
      newErrors.startDate = t('projects.creation.validation.startDateRequired');
    } else if (isDateInPast(data.startDate)) {
      newErrors.startDate = t('projects.creation.validation.startDatePast');
    }

    if (!data.endDate) {
      newErrors.endDate = t('projects.creation.validation.endDateRequired');
    } else if (data.startDate) {
      const startDate = new Date(data.startDate);
      const endDate = new Date(data.endDate);

      if (endDate <= startDate) {
        newErrors.endDate = t('projects.creation.validation.endDateAfterStart');
      }
    }

    if (data.budget !== undefined && data.budget < 0) {
      newErrors.budget = t('projects.creation.validation.budgetNegative');
    }

    setErrors(newErrors);
    const isValid = Object.keys(newErrors).length === 0;
    setValid(isValid);
    return isValid;
  }, [
    data.name,
    data.description,
    data.location?.address,
    data.startDate,
    data.endDate,
    data.budget,
    t,
    setValid,
  ]);

  // Run validation when form data changes
  useEffect(() => {
    validateForm();
  }, [validateForm]);

  // Optimized event handlers
  const handleInputEvent = useCallback(
    (field: keyof ExtendedCreateProjectData) =>
      (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const value = e.target.value;
        updateData({ [field]: value });
      },
    [updateData]
  );

  const handleTextAreaChange = useCallback(
    (field: keyof ExtendedCreateProjectData) => (value: string) => {
      updateData({ [field]: value });
    },
    [updateData]
  );

  const handleDatePickerChange = useCallback(
    (field: keyof ExtendedCreateProjectData) => (isoDate: string | null) => {
      updateData({ [field]: isoDate ? new Date(isoDate) : undefined });
    },
    [updateData]
  );

  const handleCurrencyChange = useCallback(
    (field: keyof ExtendedCreateProjectData) => (value: number) => {
      updateData({ [field]: value });
    },
    [updateData]
  );

  const handleLocationChange = useCallback(
    (location: ProjectLocation) => {
      updateData({
        location,
        // Also update the legacy address field for backward compatibility
        address: location.address,
      });
    },
    [updateData]
  );

  const handleTemplateChange = useCallback(
    (templateId: string) => {
      const selectedTemplate = templates.find(t => t.id === templateId);
      updateData({
        templateId: templateId || undefined,
        // If template is selected, pre-populate with template data
        ...(selectedTemplate && {
          tools: selectedTemplate.tools.map(tool => tool.id),
          directories: selectedTemplate.directories.map(dir => dir.id),
          customFields: selectedTemplate.customFields.reduce(
            (acc, field) => ({
              ...acc,
              [field.id]: field.defaultValue || '',
            }),
            {}
          ),
        }),
      });
    },
    [templates, updateData]
  );

  const selectedTemplate = useMemo(
    () => templates.find(t => t.id === data.templateId),
    [templates, data.templateId]
  );

  return (
    <div className="space-y-6 font-['Inter',sans-serif]">
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
        `}
      </style>

      {/* Template Selection */}
      <div className="bg-gradient-to-br from-[var(--brand-primary-light)] to-[#FFEAB8] border border-[var(--brand-primary)] border-opacity-30 rounded-lg p-6 shadow-sm">
        <div className="flex items-start space-x-3 mb-4">
          <div className="w-10 h-10 bg-[var(--brand-primary)] rounded-lg flex items-center justify-center flex-shrink-0">
            <span className="text-white font-bold text-lg">📋</span>
          </div>
          <div>
            <h4 className="text-lg font-bold text-[#1F1F1F] leading-[1.4em]">
              {t('projects.creation.projectTemplate.title')}
            </h4>
            <p className="text-sm text-[#6E7077] mt-1 font-normal leading-[1.75em]">
              {t('projects.creation.projectTemplate.description')}
            </p>
          </div>
        </div>

        <Dropdown
          options={templateOptions}
          value={data.templateId || ''}
          onChange={handleTemplateChange}
          placeholder={t('projects.creation.projectTemplate.chooseTemplate')}
          disabled={isLoadingTemplates}
        />

        {selectedTemplate && (
          <div className="mt-4 p-4 bg-white rounded-lg border border-[var(--brand-primary)] border-opacity-20 shadow-sm">
            <h5 className="font-bold text-[#1F1F1F] leading-[1.4em]">{selectedTemplate.name}</h5>
            <p className="text-sm text-[#6E7077] mt-1 font-normal leading-[1.75em]">
              {selectedTemplate.description}
            </p>
            <div className="mt-3">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-bold bg-[var(--brand-primary-light)] text-[var(--brand-primary)] uppercase tracking-wide leading-[1.2102272851126534em]">
                {t('projects.creation.projectTemplate.category')}: {selectedTemplate.category}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Basic Project Information */}
      <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 bg-brand-500 rounded-lg flex items-center justify-center">
            <span className="text-white font-bold text-lg">📝</span>
          </div>
          <div>
            <h4 className="text-lg font-bold text-[#1F1F1F] leading-[1.4em]">
              {t('projects.creation.basicInformation.title')}
            </h4>
            <p className="text-sm text-[#6E7077] mt-1 font-normal leading-[1.75em]">
              {t('projects.creation.basicInformation.description')}
            </p>
          </div>
        </div>

        <div className="space-y-6">
          <div>
            <Input
              label={t('projects.projectName')}
              placeholder={t('projects.creation.basicInformation.projectNamePlaceholder')}
              value={data.name || ''}
              onChange={handleInputEvent('name')}
              required
              error={errors.name}
            />
          </div>

          <div>
            <TextArea
              label={t('projects.description')}
              placeholder={t('projects.creation.basicInformation.projectDescriptionPlaceholder')}
              value={data.description || ''}
              onChange={handleTextAreaChange('description')}
              rows={4}
              required
              error={errors.description}
              hint={`${t('projects.creation.basicInformation.projectDescriptionHint')} (${(data.description || '').length}/2000 characters, minimum 10 required)`}
            />
          </div>

          <div>
            <LocationInput
              label={t('projects.address')}
              placeholder={t('projects.creation.basicInformation.projectAddressPlaceholder')}
              value={
                data.location || {
                  address: data.address || '',
                  city: '',
                  state: '',
                  country: '',
                  zipCode: '',
                  timezone: '',
                  coordinates: undefined,
                }
              }
              onChange={handleLocationChange}
              required
              error={errors.address}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <DatePicker
                label={t('projects.startDate')}
                value={data.startDate ? data.startDate.toISOString().split('T')[0] : null}
                onChange={handleDatePickerChange('startDate')}
                minDate={getMinDate()}
                required
                error={errors.startDate}
              />
            </div>

            <div>
              <DatePicker
                label={t('projects.endDate')}
                value={data.endDate ? data.endDate.toISOString().split('T')[0] : null}
                onChange={handleDatePickerChange('endDate')}
                minDate={data.startDate ? data.startDate.toISOString().split('T')[0] : getMinDate()}
                required
                error={errors.endDate}
              />
            </div>
          </div>

          <div>
            <CurrencyInput
              label={t('projects.budget')}
              placeholder={t('projects.creation.basicInformation.budgetPlaceholder')}
              value={data.budget || 0}
              onChange={handleCurrencyChange('budget')}
              currency={data.advanced?.currency || 'USD'}
              error={errors.budget}
            />
          </div>
        </div>
      </div>

      {/* Custom Fields from Template */}
      {selectedTemplate &&
        selectedTemplate.customFields &&
        selectedTemplate.customFields.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg">⚙️</span>
              </div>
              <div>
                <h4 className="text-lg font-bold text-[#1F1F1F] leading-[1.4em]">
                  {t('projects.creation.projectTemplate.customFields', 'Custom Fields')}
                </h4>
                <p className="text-sm text-[#6E7077] mt-1 font-normal leading-[1.75em]">
                  {t(
                    'projects.creation.projectTemplate.customFieldsDescription',
                    'Additional fields for this template'
                  )}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {selectedTemplate.customFields.map(field => (
                <div key={field.id}>
                  {field.type === 'textarea' ? (
                    <TextArea
                      label={field.label}
                      value={data.customFields?.[field.id] || ''}
                      onChange={value =>
                        updateData({
                          customFields: {
                            ...data.customFields,
                            [field.id]: value,
                          },
                        })
                      }
                      required={field.required}
                      rows={3}
                    />
                  ) : field.type === 'dropdown' ? (
                    <Dropdown
                      label={field.label}
                      options={field.options?.map(opt => ({ value: opt, label: opt })) || []}
                      value={data.customFields?.[field.id] || ''}
                      onChange={value =>
                        updateData({
                          customFields: {
                            ...data.customFields,
                            [field.id]: value,
                          },
                        })
                      }
                      required={field.required}
                    />
                  ) : (
                    <Input
                      type={field.type}
                      label={field.label}
                      value={data.customFields?.[field.id] || ''}
                      onChange={e =>
                        updateData({
                          customFields: {
                            ...data.customFields,
                            [field.id]: e.target.value,
                          },
                        })
                      }
                      required={field.required}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
    </div>
  );
};

export default ProjectDetailsStep;
